import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ShieldCheck, Lock, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const CASHFREE_JS_PROD = 'https://sdk.cashfree.com/js/v3/cashfree.js';
const RAZORPAY_JS = 'https://checkout.razorpay.com/v1/checkout.js';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(s);
  });
}

export default function PaymentLinkLanding() {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const fetchLink = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/payments/links/public/${token}`);
      setLink(res.data);
      setStatus(res.data.status);
    } catch (e) {
      setError(e.response?.data?.detail || 'Payment link not found');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLink(); }, [fetchLink]);

  const pollStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/payments/links/public/${token}/status`);
      if (res.data.status === 'paid') {
        setStatus('paid');
        toast.success('Payment successful!');
        fetchLink();
        return true;
      }
    } catch (err) {
      console.debug('Payment status poll transient error:', err?.message || err);
    }
    return false;
  }, [token, fetchLink]);

  const handleCashfreePay = async () => {
    setPaying(true);
    try {
      const initRes = await axios.post(
        `${API}/api/payments/links/public/${token}/initiate`,
        { returnUrl: window.location.href }
      );
      const { paymentSessionId, environment } = initRes.data;

      await loadScript(CASHFREE_JS_PROD);
      const mode = environment === 'production' ? 'production' : 'sandbox';
      // eslint-disable-next-line no-undef
      const cashfree = window.Cashfree({ mode });
      cashfree.checkout({
        paymentSessionId,
        redirectTarget: '_modal',
      }).then(() => {
        setPaying(false);
        // Poll for status up to 60s after modal closes
        let attempts = 0;
        const interval = setInterval(async () => {
          const paid = await pollStatus();
          attempts++;
          if (paid || attempts >= 20) clearInterval(interval);
        }, 3000);
      }).catch((err) => {
        setPaying(false);
        toast.error(err?.message || 'Payment cancelled');
      });
    } catch (e) {
      setPaying(false);
      const msg = e.response?.data?.detail || e.message;
      toast.error(msg);
      setError(msg);
    }
  };

  const handleRazorpayPay = async () => {
    setPaying(true);
    try {
      const initRes = await axios.post(
        `${API}/api/payments/links/public/${token}/initiate`,
        { returnUrl: window.location.href }
      );
      const { orderId, amount, keyId } = initRes.data;
      await loadScript(RAZORPAY_JS);
      const options = {
        key: keyId,
        amount,
        currency: 'INR',
        order_id: orderId,
        name: link.company.name,
        description: link.description,
        image: link.company.logoUrl?.startsWith('http') ? link.company.logoUrl : `${API}${link.company.logoUrl}`,
        prefill: { name: link.customerName, email: link.customerEmail },
        theme: { color: link.company.primaryColor || '#0F766E' },
        handler: async () => {
          pollStatus();
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      // eslint-disable-next-line no-undef
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      setPaying(false);
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const handlePay = () => {
    if (status !== 'pending') return;
    if (link.gateway === 'cashfree') return handleCashfreePay();
    if (link.gateway === 'razorpay') return handleRazorpayPay();
    toast.error(`Gateway ${link.gateway} not yet supported on landing page`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-teal-700" />
      </div>
    );
  }

  if (error || !link) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
          <XCircle className="w-14 h-14 text-rose-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Payment Link Unavailable</h1>
          <p className="text-slate-500 text-sm">{error || 'This link may have expired or been cancelled.'}</p>
        </div>
      </div>
    );
  }

  const primary = link.company?.primaryColor || '#0F766E';
  const logoSrc = link.company?.logoUrl?.startsWith('http')
    ? link.company.logoUrl
    : `${API}${link.company?.logoUrl || ''}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50/30 flex flex-col">
      {/* Branded Header */}
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {link.company?.logoUrl && (
              <img
                data-testid="payment-brand-logo"
                src={logoSrc}
                alt={link.company.name}
                className="h-10 w-auto object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div>
              <div className="font-bold text-slate-900 text-lg leading-tight" data-testid="payment-brand-name">
                {link.company?.name || 'Payment'}
              </div>
              {link.company?.phone && (
                <div className="text-xs text-slate-500">{link.company.phone}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <Lock className="w-3.5 h-3.5" />
            Secure Payment
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full">
          {/* Paid state */}
          {status === 'paid' && (
            <div className="bg-white rounded-3xl shadow-xl p-10 text-center border border-emerald-100">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Payment Received</h1>
              <p className="text-slate-500 text-sm mb-6">Thank you, {link.customerName}! Your payment has been successfully confirmed.</p>
              <div className="bg-emerald-50/60 rounded-xl p-5 text-left space-y-2">
                <div className="flex justify-between text-sm"><span className="text-slate-500">Amount Paid</span><span className="font-bold text-slate-900">INR {Number(link.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Reference</span><span className="font-mono text-slate-700">{link.linkId}</span></div>
              </div>
            </div>
          )}

          {/* Expired / Cancelled */}
          {(status === 'expired' || status === 'cancelled') && (
            <div className="bg-white rounded-3xl shadow-xl p-10 text-center border border-amber-100">
              <Clock className="w-14 h-14 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Link {status === 'expired' ? 'Expired' : 'Cancelled'}</h1>
              <p className="text-slate-500 text-sm">Please contact {link.company?.name} {link.company?.phone ? `at ${link.company.phone}` : ''} for a new payment link.</p>
            </div>
          )}

          {/* Active pending state */}
          {status === 'pending' && (
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100">
              {/* Amount hero */}
              <div
                className="px-8 py-10 text-white"
                style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}
              >
                <div className="text-xs uppercase tracking-wider opacity-80 mb-1">Amount Due</div>
                <div className="text-4xl font-bold tracking-tight" data-testid="payment-amount">
                  INR {Number(link.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
                <div className="mt-4 text-sm opacity-90">{link.description}</div>
              </div>

              {/* Customer details */}
              <div className="px-8 py-6 space-y-3 bg-slate-50/60 border-y border-slate-100">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Customer</span>
                  <span className="font-medium text-slate-800">{link.customerName}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Email</span>
                  <span className="font-medium text-slate-800">{link.customerEmail}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-500">Reference</span>
                  <span className="font-mono text-xs text-slate-700">{link.linkId}</span>
                </div>
              </div>

              {/* Gateway + Pay Button */}
              <div className="px-8 py-8">
                <div className="mb-5 flex items-center justify-center gap-2 text-xs text-slate-400">
                  <ShieldCheck className="w-4 h-4" />
                  <span>256-bit SSL · PCI-DSS Compliant · Powered by {link.gateway === 'cashfree' ? 'Cashfree' : link.gateway === 'razorpay' ? 'Razorpay' : 'Stripe'}</span>
                </div>
                <Button
                  data-testid="pay-now-btn"
                  onClick={handlePay}
                  disabled={paying}
                  className="w-full h-14 text-base font-bold rounded-2xl shadow-lg transition-transform active:scale-[0.99]"
                  style={{ background: primary, color: 'white' }}
                >
                  {paying ? (
                    <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Opening secure checkout…</>
                  ) : (
                    <>Pay INR {Number(link.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })} Now</>
                  )}
                </Button>
                <p className="mt-4 text-center text-xs text-slate-400">
                  Supports UPI · Credit / Debit Cards · NetBanking · Wallets · EMI
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-xs text-slate-400">
        © {new Date().getFullYear()} {link.company?.name}. All rights reserved.
      </footer>
    </div>
  );
}
